// Engineer offline data context. Holds the merged job list, the pending-sync
// count and connectivity, and owns every sync trigger so the rest of the
// engineer UI just reads state and calls complete()/syncNow().
//
// The loop: load from IndexedDB instantly (works in a no-signal basement) ->
// refresh from the server when online -> queue completions offline -> drain the
// outbox on reconnect, when the app is brought to the foreground, or when the
// service worker signals a background sync.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useOnline } from "../../lib/offline/useOnline";
import { api } from "../../lib/api";
import {
  loadMerged,
  refreshFromServer,
  enqueueCompletion,
  syncJobs,
  requestBackgroundSync,
  noteTapOnCachedJob,
  noteWorkDayTimeOnCachedJob,
} from "../../lib/offline/jobsStore";
import type { PopTagEnd } from "../../lib/popTagHome";
import { syncPhotos } from "../../lib/offline/photosStore";
import { syncMessages } from "../../lib/offline/messagesStore";
import { syncSiteList } from "../../lib/offline/historyStore";
import type { JobView, CompletionPayload } from "../../lib/offline/types";
import type { EscalatedSubmission } from "../../lib/offline/sync";
import type { EngineerTrainingSessionDto } from "../../lib/trainingTypes";
import { expandWorkDays, type DayEntry } from "../../lib/engineerWorkDays";
import { ApiError } from "../../lib/api";
import { classifyWorkDaySend, dropQueuedWorkDayTime, enqueueWorkDayTime, flushWorkDayTimes, queuedWorkDayTimeFor, recordRefusedWorkDayTime, type WorkDayTimePayload } from "../../lib/workDayTimesQueue";

interface EngineerData {
  jobs: JobView[];
  // Session BDM: the same jobs laid out by DAY: a job over several working days is one entry for each day the engineer is on (the
  // day lists read this; opening any entry opens the one job). A one-day job is exactly itself.
  dayJobs: Array<DayEntry<JobView>>;
  // Session BDM: save when the engineer arrived or left on one working day. Goes to the server when it can, waits in a small queue
  // on the device when it cannot, and is shown at once either way. Resolves true when the server has it.
  saveWorkDayTime: (jobId: string, day: string, payload: WorkDayTimePayload) => Promise<boolean>;
  // Still going to sync. Excludes escalated submissions, which are not.
  pending: number;
  // Session AEK (D32): submissions that have stopped retrying and need the office.
  escalations: EscalatedSubmission[];
  online: boolean;
  loading: boolean;
  syncing: boolean;
  // Session AFF: the unread internal-message count, polled here (once, in the provider) so BOTH the
  // shell's Messages tab badge and the home Messages tile read one value with no duplicated fetch.
  unreadMessages: number;
  // Session AGO: the engineer's scheduled training sessions, fetched here so My Jobs and the
  // calendar share one value with no duplicated fetch. LIVE-ONLY by design (Session AGG): never
  // cached in IndexedDB. Offline this holds whatever was last fetched (or [] on first load).
  trainingSessions: EngineerTrainingSessionDto[];
  // True only until the FIRST training fetch settles (or is skipped offline); later refetches are
  // silent, mirroring the unread poll.
  trainingLoading: boolean;
  // True when the last ONLINE training fetch failed. Kept so a failed load can be said out loud
  // rather than reading as "no training booked" (the Session AGN silence-looked-like-a-missing-
  // feature class); a later successful fetch clears it.
  trainingError: boolean;
  jobById: (id: string) => JobView | undefined;
  complete: (id: string, payload: CompletionPayload) => Promise<void>;
  syncNow: () => Promise<void>;
  // Session APG: a POP tag tap made from the Home screen has just landed for this job. Remembers it on
  // the cached job (so the job sheet shows that end as recorded, offline too) and reloads the list.
  // Best effort by design: the server holds the tap either way and hands the same two instants back on
  // the next job-list refresh, so a failure here costs the sheet nothing but a wait.
  noteTap: (jobId: string, end: PopTagEnd, at: string) => Promise<void>;
}

const Ctx = createContext<EngineerData | null>(null);

/** The server's plain-English refusal for a working day save, or a safe fallback. */
function workDayRefusalWords(err: ApiError): string {
  const m = err.body && typeof err.body.message === "string" ? err.body.message : null;
  return m ?? "The office could not take that. Please ring them.";
}

export function EngineerDataProvider({ children }: { children: ReactNode }) {
  const online = useOnline();
  const [jobs, setJobs] = useState<JobView[]>([]);
  const [pending, setPending] = useState(0);
  const [escalations, setEscalations] = useState<EscalatedSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [trainingSessions, setTrainingSessions] = useState<EngineerTrainingSessionDto[]>([]);
  const [trainingLoading, setTrainingLoading] = useState(true);
  const [trainingError, setTrainingError] = useState(false);

  const reload = useCallback(async () => {
    const merged = await loadMerged();
    setJobs(merged.jobs);
    setPending(merged.pending);
    setEscalations(merged.escalations);
  }, []);

  // Best-effort unread-message count. Silent on failure (also fine offline): a missing count just
  // means no badge, never an error to the engineer.
  // `background` for the 20-second poll below (Session ASB review, F4): a timer's request never keeps a session alive.
  const refreshUnread = useCallback(async (opts: { background?: boolean } = {}) => {
    try {
      const { count } = await api.messages.unreadCount(opts.background ? { background: true } : {});
      setUnreadMessages(count);
    } catch {
      /* best-effort: no badge */
    }
  }, []);

  // The engineer's scheduled training sessions (Session AGO). Live-only (Session AGG: a training
  // certificate is numbered server-side on submission, so there is nothing useful to queue
  // offline). Offline the fetch is skipped and the last-fetched list stands; a failed online fetch
  // keeps the last list and raises trainingError so the page can say so.
  const refreshTraining = useCallback(async () => {
    if (!navigator.onLine) {
      setTrainingLoading(false);
      return;
    }
    try {
      const rows = await api.training.mine();
      setTrainingSessions(rows);
      setTrainingError(false);
    } catch {
      setTrainingError(true);
    } finally {
      setTrainingLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    // Session ASB (D-SEC-6): the site list is re-pulled beside the job list on every sync, and every
    // cached history page for a site that has left it is dropped (historyStore.syncSiteList), so a site
    // whose work is finished leaves this device on the next successful sync. Started first and in
    // parallel, best-effort and silent: it never delays or fails the job list.
    const sites = syncSiteList().catch(() => {});
    try {
      await refreshFromServer();
    } catch {
      // Offline or the request failed -- keep showing the cached list.
    }
    await reload();
    await sites;
  }, [reload]);

  // Session BDM: send the working day times that were saved with no signal. Guarded inside flushWorkDayTimes; never throws.
  const syncWorkDayTimes = useCallback(async () => {
    await flushWorkDayTimes(async (q) => {
      try {
        await api.jobs.workDayTime(q.jobId, q.day, q.payload);
        return { ok: true };
      } catch (err) {
        if (err instanceof ApiError) return classifyWorkDaySend(err.status, workDayRefusalWords(err));
        return { ok: false, refused: false };
      }
    }).catch(() => null);
  }, []);

  const syncNow = useCallback(async () => {
    setSyncing(true);
    try {
      // Drain every outbox: queued completions, queued job photos AND queued messages
      // (Session ACZ). Each is internally guarded against concurrent runs. Messages drain here so a
      // queued reply sends on reconnect even if the Messages screen is not open.
      //
      // Session AFF: drain ALL THREE outboxes. The message summary was previously dropped on the
      // floor (only two of three results were destructured), so a queued reply that synced never
      // triggered the server re-pull.
      await Promise.all([syncJobs(), syncPhotos(), syncMessages(), syncWorkDayTimes()]);
      // Session ALS: when online, ALWAYS re-pull. This used to re-pull only when one of the three
      // outboxes above had actually drained something, and fell back to a cache-only reload
      // otherwise.
      //
      // That test asks whether THIS DEVICE changed the server's view. It never asked the question
      // that matters to an engineer arriving on shift: has the OFFICE changed it? A job assigned
      // from the Scheduling page is a pure server-side change with nothing in this device's outbox,
      // so the test was false and syncNow -- the handler for reconnect AND for the app being
      // foregrounded -- reloaded IndexedDB and showed the same stale list. A newly assigned job
      // appeared only on a cold start, which is the one thing an engineer who leaves the app open
      // all day never does.
      //
      // The inconsistency was already visible inside this very function: refreshUnread() and
      // refreshTraining() below are called unconditionally whenever online, so the unread badge and
      // the training calendar refreshed on foreground while the JOB LIST did not.
      //
      // refresh() reloads from cache itself after re-pulling, so the updated attempt/error state the
      // old else-branch existed to surface is picked up on this path too -- nothing is lost by no
      // longer branching. OFFLINE IS UNCHANGED: reload() alone, cache only, no request attempted.
      if (navigator.onLine) {
        await refresh();
      } else {
        await reload();
      }
      // A sync may have changed what is unread (a reply left the outbox, a fresh pull arrived), so
      // refresh the badge count off the same trigger. Training rides the same trigger (reconnect,
      // foreground, SW ping) so the schedule and calendar stay current with no fetch of their own.
      if (navigator.onLine) {
        void refreshUnread();
        void refreshTraining();
      }
    } finally {
      setSyncing(false);
    }
  }, [reload, refresh, refreshUnread, refreshTraining]);

  const complete = useCallback(
    async (id: string, payload: CompletionPayload) => {
      await enqueueCompletion(id, payload); // persisted before anything else -- never lost
      await reload();
      void requestBackgroundSync();
      if (navigator.onLine) void syncNow();
    },
    [reload, syncNow],
  );

  // Initial load: cache first (instant, offline), then refresh + sync if online. The training
  // fetch starts IN PARALLEL with the jobs refresh (never after it), and settles its own loading
  // flag offline too, so nothing ever waits on a request the device cannot make.
  useEffect(() => {
    let live = true;
    (async () => {
      await reload();
      if (live) setLoading(false);
      void refreshTraining();
      if (navigator.onLine) {
        // syncNow drains the outbox and then re-pulls (Session ALS made that unconditional when
        // online), so calling refresh() first as well would fetch /api/jobs/mine TWICE on every
        // cold start for no benefit. Draining first is also the correct order: a queued completion
        // reaches the server before the pull that would otherwise show it as still open.
        await syncNow();
      }
    })();
    return () => {
      live = false;
    };
  }, [reload, refresh, syncNow, refreshTraining]);

  // Drain triggers: reconnect, app foregrounded, or the SW's background-sync ping.
  useEffect(() => {
    const onOnline = () => void syncNow();
    const onVisible = () => {
      if (document.visibilityState === "visible") void syncNow();
    };
    const onSwMessage = (e: MessageEvent) => {
      if (e.data === "ele-flush") void syncNow();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    navigator.serviceWorker?.addEventListener("message", onSwMessage);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      navigator.serviceWorker?.removeEventListener("message", onSwMessage);
    };
  }, [syncNow]);

  // Unread-message poll (Session AFF, moved here from the shell). One place owns it, so the tab
  // badge and the home tile share a single value and a single request. Best-effort and silent, a
  // light 20s cadence, plus a refresh whenever the app is foregrounded so the badge is current when
  // the engineer picks the phone back up.
  useEffect(() => {
    void refreshUnread();
    const t = setInterval(() => void refreshUnread({ background: true }), 20000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshUnread();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshUnread]);

  const jobById = useCallback((id: string) => jobs.find((j) => j.id === id), [jobs]);
  const dayJobs = useMemo(() => expandWorkDays(jobs), [jobs]);

  const saveWorkDayTime = useCallback(
    async (jobId: string, day: string, payload: WorkDayTimePayload): Promise<boolean> => {
      // Shown at once, whatever happens next.
      await noteWorkDayTimeOnCachedJob(jobId, day, payload).catch(() => undefined);
      await reload();
      // Anything still waiting for this day is older than this save: send it underneath, so the server never gets the older word
      // later (a queued overtime flag or note replayed over a newer save).
      const merged = { ...(queuedWorkDayTimeFor(jobId, day)?.payload ?? {}), ...payload };
      try {
        if (!navigator.onLine) throw new TypeError("offline");
        await api.jobs.workDayTime(jobId, day, merged);
        dropQueuedWorkDayTime(jobId, day);
        void refresh();
        return true;
      } catch (err) {
        // A refusal the server will repeat for ever is told to the engineer now, not queued: the panel shows err.message.
        if (err instanceof ApiError) {
          const verdict = classifyWorkDaySend(err.status, workDayRefusalWords(err));
          if (!verdict.ok && verdict.refused) {
            // Session R4F (review M3): the times are kept on the phone with the refusal, not lost when the form moves on.
            dropQueuedWorkDayTime(jobId, day);
            recordRefusedWorkDayTime({ jobId, day, message: verdict.message, at: new Date().toISOString(), payload: merged });
            await refresh();
            throw new Error(verdict.message);
          }
        }
        enqueueWorkDayTime(jobId, day, { ...merged, savedAt: new Date().toISOString() });
        return false;
      }
    },
    [reload, refresh],
  );

  // Session APG: a POP tag tap made from the Home screen has landed for this job. Written to the
  // cached job, then the list is reloaded so the job sheet reads it. Swallows its own failure: the
  // tap is already on the server, and the next refresh hands the sheet the same two instants.
  const noteTap = useCallback(
    async (jobId: string, end: PopTagEnd, at: string) => {
      try {
        await noteTapOnCachedJob(jobId, end, at);
        await reload();
      } catch {
        /* best effort: the server holds the tap and the next refresh carries it */
      }
    },
    [reload],
  );

  const value = useMemo<EngineerData>(
    () => ({
      jobs,
      dayJobs,
      saveWorkDayTime,
      pending,
      escalations,
      online,
      loading,
      syncing,
      unreadMessages,
      trainingSessions,
      trainingLoading,
      trainingError,
      jobById,
      complete,
      syncNow,
      noteTap,
    }),
    [jobs, dayJobs, saveWorkDayTime, pending, escalations, online, loading, syncing, unreadMessages, trainingSessions, trainingLoading, trainingError, jobById, complete, syncNow, noteTap],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEngineerData(): EngineerData {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useEngineerData must be used within an EngineerDataProvider");
  return ctx;
}
